import {
    Card,
    Dialog,
    DialogContent,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    ActionButton,
} from "@jhool-io/fe-components";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useIsMutating } from "@tanstack/react-query";
import ListState from "../../../../../components/ListState/ListState";
import UpcomingAppointment from "./UpcomingAppointment/UpcomingAppointment";
import {
    getStartAndEndOfWeekInAppFormat,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
} from "../../../../../utils/helpers";
import ConfirmScheduleForm from "../../ConfirmScheduleForm/ConfirmScheduleForm";
import CalendarIcon from "../../../../../icons/Calendar";
import Loader from "../../../../../shared-ui/Loader/Loader";
import PlusIcon from "../../../../../icons/Plus";
import { ScheduleWeekType } from "../../../../../utils/types";
import { useFetchWeeklySchedule } from "../../../../schedule/hooks/schedule.queries";
import { useAppSelector } from "../../../../../hooks/useRedux";
import { useCanCreateNote } from "../../../../notes/hooks/useCanCreateNote";

export default function UpcomingAppointments() {
    const [showDialog, setShowDialog] = useState(false);
    const [weekInDisplay, setWeekInDisplay] = useState(
        ScheduleWeekType.THIS_WEEK
    );

    const navigate = useNavigate();

    const [searchParams, setSearchParams] = useSearchParams();

    const { practice } = useAppSelector((state) => state.userPractice);
    const canCreateNote = useCanCreateNote();

    const { startOfWeekDisplay: startOfWeek, endOfWeekDisplay: endOfWeek } =
        getStartAndEndOfWeekInAppFormat(weekInDisplay, {
            start: "yyyy-MM-dd",
            end: "yyyy-MM-dd",
        });

    const paymentFilter = searchParams.get("payment") || "";

    const updateScheduleIsMutating = useIsMutating(["update-schedule"]);

    const {
        data: appointmentsData,
        isLoading: appointmentsIsLoading,
        error: appointmentsError,
        isSuccess: appointmentsIsSuccess,
    } = useFetchWeeklySchedule(
        {
            from_date: startOfWeek,
            to_date: endOfWeek,
            provider_ids: [practice?.provider_id || ""],
            schedule_for_form: true,
        },
        Boolean(practice?.provider_id)
    );

    const handleSetPaymentFilter = (payment: string) => {
        if (payment === "") searchParams.delete("payment");
        else searchParams.set("payment", payment);

        setSearchParams(searchParams);
    };

    const filteredSessions = (appointmentsData?.data ?? [])
        .filter((session) => {
            if ("confirmed_start_date_time" in session) {
                return Boolean(session.confirmed_start_date_time);
            }
            return true;
        })
        .filter((session) => {
            if (paymentFilter === "paid") return session.is_paid;
            if (paymentFilter === "unpaid") return !session.is_paid;
            return true;
        });

    const sortedSessions = filteredSessions.sort((a, b) => {
        if (
            "confirmed_start_date_time" in a &&
            "confirmed_start_date_time" in b
        ) {
            // Get the full date objects for comparison
            const dateA = new Date(a.confirmed_start_date_time as string);
            const dateB = new Date(b.confirmed_start_date_time as string);

            // Get day of week (using UTC to avoid timezone issues)
            const dayA = dateA.getUTCDay();
            const dayB = dateB.getUTCDay();

            // Convert so Monday = 0, Sunday = 6
            const adjustedDayA = dayA === 0 ? 6 : dayA - 1;
            const adjustedDayB = dayB === 0 ? 6 : dayB - 1;

            // First sort by day of week
            if (adjustedDayA !== adjustedDayB) {
                return adjustedDayA - adjustedDayB;
            }

            // If same day of week, sort by actual date and time
            return dateA.getTime() - dateB.getTime();
        }
        return 0;
    });

    return (
        <>
            <Dialog open={showDialog}>
                <DialogContent
                    title="Manage this week’s schedule"
                    variant="center"
                    handleCloseDialog={() => setShowDialog(false)}
                    wrapperClass="w-[800px]"
                    saveText="Confirm schedule"
                    submitBtnFormValue="confirm-schedule"
                    isSubmitBtnDisabled={updateScheduleIsMutating > 0}
                    isCancelBtnDisabled={updateScheduleIsMutating > 0}
                >
                    <ConfirmScheduleForm
                        onFormSubmit={() => setShowDialog(false)}
                    />
                </DialogContent>
            </Dialog>
            <Card classNames="flex flex-col relative overflow-y-auto h-[400px] lg:h-[430px]">
                <div className="sticky top-0 left-0 z-[5]">
                    <div className="flex justify-between items-center flex-wrap gap-[--spacing-items] pb-[--padding-card-y-lg] [border-bottom:1px_solid_var(--color-stroke)]">
                        <h3 className="text-base font-medium tracking-[-0.176px]">
                            Upcoming Appointment
                        </h3>
                        <div className="flex gap-[--spacing-items] items-center flex-wrap">
                            <Dropdown>
                                <DropdownTrigger
                                    asChild
                                    disabled={appointmentsIsLoading}
                                >
                                    <FilterButton>
                                        {paymentFilter
                                            ? makeStringFirstLetterCapital(
                                                  paymentFilter
                                              )
                                            : "Payment"}
                                    </FilterButton>
                                </DropdownTrigger>
                                <DropdownContent width={101}>
                                    <DropdownItem
                                        onClick={() =>
                                            handleSetPaymentFilter("")
                                        }
                                        isSelected={!paymentFilter}
                                    >
                                        All
                                    </DropdownItem>
                                    <DropdownItem
                                        onClick={() =>
                                            handleSetPaymentFilter("paid")
                                        }
                                        isSelected={paymentFilter === "paid"}
                                    >
                                        Paid
                                    </DropdownItem>
                                    <DropdownItem
                                        onClick={() =>
                                            handleSetPaymentFilter("unpaid")
                                        }
                                        isSelected={paymentFilter === "unpaid"}
                                    >
                                        Unpaid
                                    </DropdownItem>
                                </DropdownContent>
                            </Dropdown>
                            <Dropdown>
                                <DropdownTrigger asChild>
                                    <FilterButton>
                                        {removeEnumUnderscore(weekInDisplay)}
                                    </FilterButton>
                                </DropdownTrigger>
                                <DropdownContent>
                                    {Object.values(ScheduleWeekType).map(
                                        (type) => (
                                            <DropdownItem
                                                key={type}
                                                onClick={() => {
                                                    setWeekInDisplay(type);
                                                }}
                                            >
                                                {removeEnumUnderscore(type)}
                                            </DropdownItem>
                                        )
                                    )}
                                </DropdownContent>
                            </Dropdown>

                            {canCreateNote ? (
                                <ActionButton
                                    onClick={() => navigate("/add-note")}
                                    iconLeft={<PlusIcon />}
                                    actionTitle="Create a new note"
                                    className="cursor-pointer"
                                >
                                    Create Note
                                </ActionButton>
                            ) : null}
                            <ActionButton
                                onClick={() => setShowDialog(true)}
                                iconLeft={<CalendarIcon />}
                                actionTitle="Manage this week's schedule"
                            >
                                Confirm Schedule
                            </ActionButton>
                        </div>
                    </div>
                </div>
                <div className="flex-1 py-[--spacing-items] overflow-y-auto">
                    {appointmentsIsLoading ? (
                        <Loader wrapperClassName="h-full" />
                    ) : null}
                    {appointmentsError ? (
                        <ListState
                            isError
                            errorMsg={
                                appointmentsError?.response?.data?.message ||
                                "Could not load schedule at this time"
                            }
                            stateHelperText="Try reloading this page to solve this issue"
                            context="general"
                            cardClass="h-full"
                        />
                    ) : null}
                    {appointmentsIsSuccess && sortedSessions.length === 0 ? (
                        <ListState
                            stateHelperText={`Click the "+ Confirm schedule" button to create and confirm your schedule for the week.`}
                            isError={false}
                            emptyMessage="No schedule found"
                            context="general"
                            emptyBtnProps={{
                                showButton: true,
                                buttonText: (
                                    <>
                                        <PlusIcon />
                                        Confirm schedule
                                    </>
                                ),
                                buttonClass: "gap-x-1",
                                onButtonClick: () => setShowDialog(true),
                            }}
                            cardClass="h-full"
                        />
                    ) : null}
                    {appointmentsIsSuccess && sortedSessions.length > 0 ? (
                        <div className="flex flex-col gap-y-[--spacing-items]">
                            {sortedSessions.map((appointment, index) => (
                                <UpcomingAppointment
                                    key={`${appointment.session_id} ${
                                        index + 1
                                    }`}
                                    appointment={appointment}
                                />
                            ))}
                        </div>
                    ) : null}
                </div>
            </Card>
        </>
    );
}
